import { LocationIcon, TrashIcon } from '@primer/octicons-react';
import { Box, IconButton, Spinner, Tooltip } from '@primer/react';
import { allGeolocationSiteTypes, allSupplyChainPositions } from '@upright/common/model/input/companies/geolocation';
import {
  NO_GEOCODE_VERDICT,
  siteCoordinateCheck,
} from '@upright/common/model/input/companies/geolocation/coordinate-check';
import { TooltipBadge } from '@upright/ui/common/generic/components/badge';
import { InfoTooltip } from '@upright/ui/common/generic/components/tooltip';
import { Text } from '@upright/ui/common/generic/components/typography';
import { SelectorCell } from '@upright/ui/common/generic/components/operational-data-input/shared/cells/country-selector-cell';
import { DMA_COUNTRIES } from '@upright/common/model/input/companies/operational-data/dma-countries';
import { EmptyState } from '@upright/ui/common/generic/components/operational-data-input/shared/form-layout-styles';
import { TableV2 } from '@upright/ui/common/generic/content/tables/table-v2';
import { ColumnV2 } from '@upright/ui/common/generic/content/tables/table-v2/types';
import { CSSProperties, ReactNode, RefObject, useLayoutEffect, useMemo, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { ClampedTextCell } from './cells/clamped-text-cell';
import { EditableNumberCell } from './cells/editable-number-cell';
import { EditableTextCell } from './cells/editable-text-cell';
import { COORDINATE_CHECK_LABEL, coordinateCheckExplanation } from './coordinate-check-copy';
import { SiteRow } from './types';

/**
 * Generic over the row so a caller can carry its own per-row state alongside the site fields, as
 * the chat proposal card does for which saved site a row edits; the table itself reads only the
 * `SiteRow` fields.
 */
export interface SiteLocationsTableProps<Row extends SiteRow = SiteRow> {
  data: Row[];
  isEditing: boolean;
  isLoading: boolean;
  onUpdateRow: (rowUiId: string | undefined, updates: Partial<Row>) => void;
  onDeleteRow: (rowUiId: string | undefined) => void;
  /** Geocode a row's address into its latitude/longitude (PLT-5329 #8 — user-triggered). */
  onIdentifyFromAddress: (row: Row) => void;
  /** `_uiId` of the row whose coordinates are currently being identified, else null. */
  geocodingRowId: string | null;
  /** _uiId -> set of field names that failed save-time validation, to flag the offending cells. Partial: only rows with errors have an entry. */
  rowErrors: Partial<Record<string, Set<keyof SiteRow>>>;
  /**
   * `page` (default) is the dashboard's site-locations page: two-line cells, and pages of 10.
   *
   * `review-step` is the custom-company wizard's sites step (PLT-6998), where every identified
   * site has to be seen before publishing:
   * - every row in one box instead of pages, so no site waits on a second page; the box scrolls
   *   vertically past `REVIEW_STEP_VISIBLE_ROWS` rows, with the header pinned, and gives up
   *   height to the step around it when the step is shorter than that, down to
   *   `REVIEW_STEP_MIN_VISIBLE_ROWS`, so on a laptop screen it is the one thing that scrolls;
   * - text on one line, truncated with the full value in a `title` (DESIGN.md: labels in dense
   *   lists truncate, they do not wrap or clamp), so rows keep one height and scan as a list.
   *
   * On both, each column has a minimum width in pixels, wide enough for its header and its typical
   * value, growing in proportion when there is room; when the columns need more than the table is
   * wide, the table scrolls sideways rather than squeezing them (PLT-7887).
   */
  layout?: 'page' | 'review-step';
  /**
   * An extra first column, for a caller with something to say about each row that the table does
   * not know: the chat proposal card's Edited / New / Unchanged. `width` is a minimum in pixels, as
   * in the review step, the only layout it is meant for. `cell` must be stable across renders (a
   * `useCallback`), or every render rebuilds the columns and an edited cell loses focus after one
   * character. The table's own Coordinates column, when a row has one to show, comes after it.
   */
  leadingColumn?: { header: string; width: number; cell: (row: Row) => ReactNode };
}

/** How many rows the `review-step` box shows before it scrolls. */
const REVIEW_STEP_VISIBLE_ROWS = 10;

/**
 * How many rows the `review-step` box keeps in view however short the step: below this the step
 * body scrolls instead, since a box of one or two rows is too small to review in.
 */
const REVIEW_STEP_MIN_VISIBLE_ROWS = 3;

interface ReviewStepBounds {
  /**
   * The scroll box's cap: header plus the first `REVIEW_STEP_VISIBLE_ROWS` rows; null while there
   * are no more rows.
   */
  boxMaxHeight: number | null;
  /**
   * The wrapper's floor: the scroll box at header plus the first `REVIEW_STEP_MIN_VISIBLE_ROWS`
   * rows (the whole table when shorter), plus what TableV2 renders below the box. It sits on the
   * wrapper because the wrapper is the box the step body shrinks: a floor on a box further in does
   * not stop the boxes around it shrinking past it, and they then clip it.
   */
  wrapperMinHeight: number | null;
}

const NO_BOUNDS: ReviewStepBounds = { boxMaxHeight: null, wrapperMinHeight: null };

/**
 * The `review-step` height bounds. Measured, not multiplied out: a row's height depends on how its
 * text wraps and on edit mode, so a fixed per-row figure either cuts off the last row that should
 * fit or lets more in. Re-measured whenever the table resizes (adding or deleting a row, entering
 * edit mode, a narrower window) and whenever its rows move (sorting by a column).
 */
function useReviewStepBounds(wrapperRef: RefObject<HTMLDivElement>, enabled: boolean): ReviewStepBounds {
  const [bounds, setBounds] = useState<ReviewStepBounds>(NO_BOUNDS);
  useLayoutEffect(() => {
    const table = wrapperRef.current?.querySelector('table');
    if (!enabled || !table) return;
    const measure = () => {
      // A row's extent is its first cell's: thead, tbody and tr are display: contents, and the
      // cells are the grid items. Relative to the table's top, so scrolling does not change it.
      const firstCells = table.querySelectorAll('tbody tr > td:first-child');
      const tableTop = table.getBoundingClientRect().top;
      // The box is border-box, and a classic horizontal scrollbar takes its height from inside
      // it, so both are added back or they would cut into the last row.
      const box = table.parentElement;
      const boxChrome = box ? box.offsetHeight - box.clientHeight : 0;
      const heightThrough = (cell: Element) => cell.getBoundingClientRect().bottom - tableTop + boxChrome;

      const boxMaxHeight =
        firstCells.length > REVIEW_STEP_VISIBLE_ROWS ? heightThrough(firstCells[REVIEW_STEP_VISIBLE_ROWS - 1]) : null;
      // An empty table has no rows, only its empty state, so its whole height is the floor.
      const floorRows = Math.min(firstCells.length, REVIEW_STEP_MIN_VISIBLE_ROWS);
      const boxMinHeight =
        floorRows > 0 ? heightThrough(firstCells[floorRows - 1]) : table.getBoundingClientRect().height + boxChrome;
      // Below the box's background wrap TableV2 renders a bottom row (empty here, but with a top
      // margin). Measured from the wrap's bottom, which moves with the box's height, so the figure
      // holds however far the box has shrunk. Rounded up, so a sub-pixel shortfall cannot cut into
      // the box.
      const boxWrap = box?.parentElement;
      const belowBox = boxWrap?.nextElementSibling;
      const belowBoxHeight =
        boxWrap && belowBox ? belowBox.getBoundingClientRect().bottom - boxWrap.getBoundingClientRect().bottom : 0;
      const wrapperMinHeight = Math.ceil(boxMinHeight + belowBoxHeight);

      // Unchanged bounds keep the same object, so React skips the render.
      setBounds(current =>
        current.boxMaxHeight === boxMaxHeight && current.wrapperMinHeight === wrapperMinHeight
          ? current
          : { boxMaxHeight, wrapperMinHeight },
      );
    };
    measure();
    // The cap is on the scroll box around the table, so applying it never resizes the table itself.
    const resizeObserver = new ResizeObserver(measure);
    resizeObserver.observe(table);
    // Sorting by a column reorders the rows without resizing the table (Uptight), so row moves are
    // watched too. Measuring an unchanged layout sets the same height, which React skips.
    const mutationObserver = new MutationObserver(measure);
    mutationObserver.observe(table, { childList: true, subtree: true });
    return () => {
      resizeObserver.disconnect();
      mutationObserver.disconnect();
    };
  }, [wrapperRef, enabled]);
  return bounds;
}

/**
 * TableV2's root, as a column that can shrink: the step's body is a column flex box, and the
 * scroll box can only give up height if every box between them can. Its own vertical scrolling is
 * off: a scroll container between the step body and the table's scroll box would clip the box and
 * nest a second scrollbar inside the step.
 */
const REVIEW_STEP_ROOT_STYLE: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  minHeight: 0,
  overflowY: 'visible',
};

/** Whether a row has any address part to geocode from. */
export function rowHasAddress(row: SiteRow): boolean {
  return [row.streetAddress, row.addressLocality, row.addressRegion, row.postalCode, row.addressCountry].some(
    part => !!part && part.trim() !== '',
  );
}

/**
 * Site-specific table for the site-locations input page (PLT-5329). The generic
 * operational-data table is percentage-specific, so this is a parallel table that reuses the
 * shared cell/edit-mode building blocks (the generic country `SelectorCell`, the
 * delete-row icon button, the TableV2 wrapper) but lays out the geolocation-site columns.
 *
 * In view mode every cell is plain text; in edit mode the editable cells render. Coordinates
 * and weight are numeric; `addressCountry` uses the shared country picker; `supplyChainPosition`
 * uses the same picker over the fixed `allSupplyChainPositions` enum.
 */
export function SiteLocationsTable<Row extends SiteRow = SiteRow>({
  data,
  isEditing,
  isLoading,
  onUpdateRow,
  onDeleteRow,
  onIdentifyFromAddress,
  geocodingRowId,
  rowErrors,
  layout = 'page',
  leadingColumn,
}: SiteLocationsTableProps<Row>) {
  const isReviewStep = layout === 'review-step';
  // PLT-7875: outside edit mode the Coordinates column is there only while some row has something to
  // flag, as the chat card drops its status column when it has nothing to say. In edit mode it is
  // always there: typing a row's last coordinate clears its flag, and a column that then vanished
  // would rebuild every column and take the focus out of the cell being typed in. A boolean, so the
  // columns below are rebuilt when it flips rather than on every keystroke that changes `data`.
  const hasCoordinateChecks = useMemo(() => data.some(row => siteCoordinateCheck(row) !== null), [data]);
  const showsCoordinateChecks = isEditing || hasCoordinateChecks;

  const columns: Array<ColumnV2<Row>> = useMemo(() => {
    const hasError = (row: Row, field: keyof SiteRow) => rowErrors[row._uiId ?? '']?.has(field) ?? false;

    // An empty cell reads "—" in the muted tier, not blank (design/50-components.md, "Tables"): a
    // blank reads as a cell that failed to load, and beside a filled one as an arbitrary gap.
    const viewText = (value: string | null) =>
      value === null || value.trim() === '' ? (
        <NoValue />
      ) : isReviewStep ? (
        <SingleLine>
          <Text size="md" height="sm" truncate>
            {value}
          </Text>
        </SingleLine>
      ) : (
        <ClampedTextCell value={value} />
      );

    const textColumn = (
      field: 'name' | 'description' | 'streetAddress' | 'addressLocality' | 'addressRegion' | 'postalCode',
      header: string,
      columnWidth: number,
      placeholder?: string,
    ): ColumnV2<Row> => ({
      id: field,
      header,
      accessorKey: field,
      cell: cellProps => {
        const row = cellProps.row.original;
        const value = (cellProps.getValue() as string | null) ?? null;
        if (!isEditing) return viewText(value);
        return (
          <EditableTextCell
            value={value}
            onUpdate={newValue => {
              onUpdateRow(row._uiId, { [field]: newValue } as Partial<Row>);
            }}
            placeholder={placeholder}
            hasError={hasError(row, field)}
          />
        );
      },
      meta: { width: columnWidth },
    });

    const numberColumn = (
      field: 'latitude' | 'longitude' | 'weight',
      header: string,
      columnWidth: number,
      min?: number,
      placeholder?: string,
      /** Explanation shown behind an info icon beside the header. */
      headerTooltip?: ReactNode,
    ): ColumnV2<Row> => ({
      id: field,
      // The design-system `InfoTooltip` (`components/tooltip/`, react-aria) rather
      // than the legacy tippy one in `components/tooltips/`: it portals its own
      // overlay, so nothing has to be done about the table container clipping it,
      // and it opens on hover and on keyboard focus. Keep the text to a phrase —
      // it renders inside a single `Text`, and the rules that need more room than
      // that belong above the table, not behind an icon.
      header: headerTooltip
        ? () => (
            <HeaderWithTooltip>
              {header}
              <InfoTooltip size="small" text={headerTooltip} />
            </HeaderWithTooltip>
          )
        : header,
      accessorKey: field,
      cell: cellProps => {
        const row = cellProps.row.original;
        const value = (cellProps.getValue() as number | null) ?? null;
        if (!isEditing) {
          if (value === null) return <NoValue />;
          return isReviewStep ? viewText(String(value)) : <span>{value}</span>;
        }
        return (
          <EditableNumberCell
            value={value}
            onUpdate={newValue => {
              // A typed coordinate is its author's claim, so the geocoder's verdict on the one it
              // replaces goes with it (PLT-7875).
              const verdict = field === 'weight' ? {} : NO_GEOCODE_VERDICT;
              onUpdateRow(row._uiId, { [field]: newValue, ...verdict } as Partial<Row>);
            }}
            hasError={hasError(row, field)}
            min={min}
            placeholder={placeholder}
          />
        );
      },
      meta: { width: columnWidth, align: 'right' },
    });

    // Column order is product-driven (PLT-5329 iteration): Location name, Location description,
    // Value chain, Site type, then the address parts (Country → Region → City → Postal code →
    // Street address), then coordinates and weight. Evidence URL is intentionally not surfaced.
    //
    // Each width is a minimum in pixels, which TableV2 grows in proportion when the table has room
    // and scrolls sideways past when it has not. The minimums are each column's header, bold, plus
    // its cell padding, raised where the typical value is wider than the header: a site type, a
    // street address, a seven-decimal coordinate. The two free-text columns get the most, since
    // they identify the row; their longer values truncate (on the page, after two lines).
    const cols: Array<ColumnV2<Row>> = [
      textColumn('name', 'Location name', 150),
      textColumn('description', 'Location description', 200),
      {
        id: 'supplyChainPosition',
        header: 'Value chain',
        accessorKey: 'supplyChainPosition',
        cell: cellProps => {
          const row = cellProps.row.original;
          if (!isEditing) return viewText(row.supplyChainPosition);
          return (
            <SelectorCell
              value={row.supplyChainPosition}
              onUpdate={newValue => {
                onUpdateRow(row._uiId, {
                  supplyChainPosition: newValue as SiteRow['supplyChainPosition'],
                } as Partial<Row>);
              }}
              availableValues={allSupplyChainPositions}
              placeHolder="Select position"
            />
          );
        },
        meta: { width: 104 },
      },
      {
        id: 'locationType',
        header: 'Site type',
        accessorKey: 'locationType',
        cell: cellProps => {
          const row = cellProps.row.original;
          if (!isEditing) return viewText(row.locationType);
          return (
            <SelectorCell
              value={row.locationType}
              onUpdate={newValue => {
                onUpdateRow(row._uiId, { locationType: newValue } as Partial<Row>);
              }}
              availableValues={allGeolocationSiteTypes}
              placeHolder="Select site type"
              // Site-type labels (e.g. "Corporate/administrative") are wider than the column,
              // so widen the open menu past the cell width to stop them wrapping.
              menuMinWidth="240px"
            />
          );
        },
        meta: { width: 140 },
      },
      {
        id: 'addressCountry',
        header: 'Country',
        accessorKey: 'addressCountry',
        cell: cellProps => {
          const row = cellProps.row.original;
          const value = row.addressCountry ?? '';
          if (!isEditing) return viewText(value);
          return (
            <SelectorCell
              value={value}
              onUpdate={newValue => {
                onUpdateRow(row._uiId, { addressCountry: newValue } as Partial<Row>);
              }}
              availableValues={DMA_COUNTRIES}
              placeHolder="Select country"
            />
          );
        },
        // 120 for the edit-mode picker's "Select country", which wraps any narrower.
        meta: { width: 120 },
      },
      textColumn('addressRegion', 'Region', 112),
      textColumn('addressLocality', 'City', 112),
      textColumn('postalCode', 'Postal code', 100),
      textColumn('streetAddress', 'Street address', 160),
      // The placeholders carry four decimals deliberately: they are the only precision
      // guidance visible at the moment of typing, and demonstrating the form is read
      // where a sentence about decimal places is not. The page description explains why.
      // 112 holds a signed seven-decimal coordinate such as -122.1478778 on one line; at 100 it
      // wraps on the page and truncates in the review step.
      numberColumn('latitude', 'Latitude', 112, undefined, '40.4637'),
      numberColumn('longitude', 'Longitude', 112, undefined, '-3.7492'),
      // "(%)" is honest because publishing refuses a position that adds up to more
      // than 100 (PLT-6333, `findOverWeightedPositions`): a weight is always a
      // percentage of its own value-chain position, never a relative share. 136 keeps the header
      // and its info icon on one line when this is the last column, with the frame's wider padding.
      numberColumn(
        'weight',
        'Weight (%)',
        136,
        0,
        undefined,
        "Each site's percentage share of its own value-chain position; internal, upstream and downstream each run to 100% separately.",
      ),
    ];

    if (isEditing) {
      // Per-row "Identify from address" action (PLT-5329 #8). Sits right after the coordinate
      // columns. Disabled when the row has no address to geocode from, or while any row is being
      // identified (the geocode calls share one in-flight slot). A spinner replaces the icon for
      // the row currently resolving.
      const identifyColumn: ColumnV2<Row> = {
        id: 'identify',
        header: '',
        cell: cellProps => {
          const row = cellProps.row.original;
          const isThisRowGeocoding = geocodingRowId === row._uiId;
          const canIdentify = rowHasAddress(row);
          return (
            <Box display="flex" sx={{ justifyContent: 'center' }}>
              {isThisRowGeocoding ? (
                <Spinner size="small" />
              ) : (
                <Tooltip
                  text={canIdentify ? 'Identify coordinates from address' : 'Add an address first'}
                  direction="w"
                >
                  <IconButton
                    icon={LocationIcon}
                    size="small"
                    variant="invisible"
                    aria-label="Identify coordinates from address"
                    disabled={!canIdentify || geocodingRowId !== null}
                    onClick={() => {
                      onIdentifyFromAddress(row);
                    }}
                  />
                </Tooltip>
              )}
            </Box>
          );
        },
        meta: { width: 48, align: 'center' },
      };
      const longitudeIndex = cols.findIndex(col => col.id === 'longitude');
      cols.splice(longitudeIndex + 1, 0, identifyColumn);

      cols.push({
        id: 'delete',
        header: '',
        cell: cellProps => (
          // TODO: Box is deprecated, consider changing to div https://github.com/primer/react/blob/main/contributor-docs/migration-from-box.md
          <Box display="flex" sx={{ justifyContent: 'flex-end' }}>
            <IconButton
              icon={TrashIcon}
              size="small"
              variant="invisible"
              aria-label="Delete row"
              onClick={() => {
                onDeleteRow(cellProps.row.original._uiId);
              }}
            />
          </Box>
        ),
        meta: { width: 48, align: 'right' },
      });
    }

    // PLT-7875: first, so the flag is in view however far the table scrolls sideways; the
    // coordinates it is about are near the far end. After a caller's own leading column, which says
    // what the row is before this says what is wrong with it.
    if (showsCoordinateChecks) {
      cols.unshift({
        id: 'coordinateCheck',
        header: 'Coordinates',
        cell: cellProps => <CoordinateCheckCell row={cellProps.row.original} />,
        meta: { width: 128 },
      });
    }

    if (leadingColumn) {
      cols.unshift({
        id: 'leading',
        header: leadingColumn.header,
        cell: cellProps => leadingColumn.cell(cellProps.row.original),
        meta: { width: leadingColumn.width },
      });
    }

    return cols;
  }, [
    isEditing,
    onUpdateRow,
    onDeleteRow,
    onIdentifyFromAddress,
    geocodingRowId,
    rowErrors,
    isReviewStep,
    leadingColumn,
    showsCoordinateChecks,
  ]);

  const wrapperRef = useRef<HTMLDivElement>(null);
  const { boxMaxHeight, wrapperMinHeight } = useReviewStepBounds(wrapperRef, isReviewStep);

  const emptyState = <EmptyState>{'No sites yet. Click "Edit table" and add a site.'}</EmptyState>;
  return (
    <StyledTableWrapper
      ref={wrapperRef}
      $pinHeader={isReviewStep}
      $shrinks={isReviewStep}
      $minHeight={wrapperMinHeight}
    >
      {!isReviewStep ? (
        <TableV2
          columns={columns}
          data={data}
          pageSize={10}
          showPageSizeSelector="always"
          pageSizeOptions={[10, 25, 50, 100]}
          emptyState={emptyState}
          isLoading={isLoading}
        />
      ) : (
        <TableV2
          columns={columns}
          data={data}
          pageSize={false}
          // DESIGN.md: new tables use the plain variant (white header, semibold labels).
          variant="plain"
          emptyState={emptyState}
          isLoading={isLoading}
          style={REVIEW_STEP_ROOT_STYLE}
          tableAreaStyles={{
            back: css`
              display: flex;
              flex-direction: column;
              min-height: 0;
            `,
            // TableV2's own scroll box, which already scrolls sideways once the columns' minimum
            // widths exceed it, gets the height cap too, so one box scrolls both ways. Its floor
            // is the wrapper's. Its right-edge fade is a mask over the whole box, scrollbars
            // included, so it is off here: it would fade out the vertical scrollbar it sits over.
            front: css`
              ${boxMaxHeight === null ? '' : `max-height: ${boxMaxHeight}px;`}
              overflow-y: auto;
              mask-image: none;
            `,
          }}
        />
      )}
    </StyledTableWrapper>
  );
}

const StyledTableWrapper = styled.div<{ $pinHeader: boolean; $shrinks: boolean; $minHeight: number | null }>`
  ${props =>
    props.$shrinks &&
    css`
      /* A shrinkable column inside the step's column, for the scroll box below (see
         REVIEW_STEP_ROOT_STYLE), down to the three-row floor; past that the step body scrolls. */
      display: flex;
      flex-direction: column;
      min-height: ${props.$minHeight ?? 0}px;
    `}

  th,
  td {
    font-size: var(--font-size__md) !important;
  }

  th {
    font-weight: var(--font-weight__bold) !important;
    background-color: var(--bgColor-default) !important;
  }

  /* The header cells are grid items of the table (thead and tr are display: contents), so they
     pin to the top of the scroll box. The background above keeps rows from showing through. */
  ${props =>
    props.$pinHeader &&
    css`
      th {
        position: sticky;
        top: 0;
        z-index: 1;
      }
    `}
`;

/**
 * The cell is a flex box; `truncate` needs a block of definite width to ellipsise against, as in
 * the typography story, so this fills the cell and lets it shrink.
 */
const SingleLine = styled.span`
  display: block;
  width: 100%;
  min-width: 0;
`;

/** Keeps the info icon on the header's baseline instead of wrapping under it. */
const HeaderWithTooltip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: var(--space__xs);
`;

/** The no-data marker for an empty view-mode cell. */
function NoValue() {
  return (
    <Text size="md" height="sm" color="gray-600">
      —
    </Text>
  );
}

/**
 * PLT-7875: a neutral badge naming what is wrong with the row's coordinates, with what it costs and
 * how to fix it in the tooltip; empty when there is nothing to say. A `TooltipBadge`, so the
 * explanation is a tab stop rather than hover-only.
 */
function CoordinateCheckCell({ row }: { row: SiteRow }) {
  const check = siteCoordinateCheck(row);
  if (check === null) return null;
  const label = COORDINATE_CHECK_LABEL[check];
  return (
    <TooltipBadge tone="neutral" label={`Coordinates: ${label}`} tooltip={coordinateCheckExplanation(row, check)}>
      {label}
    </TooltipBadge>
  );
}
